/**
 * Вручение заказа.
 *
 * Сумма предзаполнена итогом, но её можно исправить: расхождение — штатная
 * ситуация (сдачи не нашлось, клиент доплатил), и администратор увидит его в
 * журнале. Запрещать ввод другой суммы значит заставить курьера соврать.
 */
import { useState } from 'react';
import type { CurrentUser, Order } from '@arbuz/shared';
import { ApiError, api } from '../api';
import { formatMoney, makeT } from '../i18n';
import { haptic } from '../tg';
import { inputStyle, labelStyle, rowStyle } from './courierUi';

interface Props {
  user: CurrentUser;
  order: Order;
  onDone: () => void;
}

export function CourierDeliver({ user, order, onDone }: Props) {
  const t = makeT(user.locale);
  // Копейки в ценах нулевые — показываем целое, как и везде в интерфейсе
  const [amount, setAmount] = useState(String(Math.round(Number(order.total))));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const normalized = amount.replace(/\s/g, '').replace(',', '.');
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
      setError(t('amountInvalid'));
      return;
    }

    haptic('success');
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/courier/orders/${order.id}/deliver`, {
        collected_amount: normalized,
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('actionFailed'));
      setBusy(false);
    }
  }

  return (
    <main className="page">
      <h1 className="h1">{t('deliverTitle')}</h1>

      <section className="card">
        <div style={{ ...rowStyle, marginTop: 0 }}>
          <span className="muted">{t('courierOrder')}</span>
          <strong>#{order.public_number}</strong>
        </div>
        <div style={rowStyle}>
          <span className="muted">{t('total')}</span>
          <strong>
            {formatMoney(order.total, user.locale)} {t('sum')}
          </strong>
        </div>
        <div style={rowStyle}>
          <span className="muted">{t('paymentTitle')}</span>
          <span>{order.payment_method === 'cash' ? t('paymentCash') : t('paymentCard')}</span>
        </div>
      </section>

      <div style={{ marginTop: 'var(--s-4)' }}>
        <label htmlFor="collected" style={labelStyle}>
          {t('collectedLabel')}
        </label>
        <input
          id="collected"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          autoComplete="off"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'collected-err' : 'collected-hint'}
          style={{
            ...inputStyle,
            borderColor: error ? 'var(--c-danger)' : 'var(--c-border-input)',
          }}
        />
        {error ? (
          <div
            id="collected-err"
            role="alert"
            style={{
              color: 'var(--c-danger)',
              fontSize: 'var(--fs-sm)',
              marginTop: 'var(--s-1)',
            }}
          >
            {error}
          </div>
        ) : (
          <p
            id="collected-hint"
            className="muted"
            style={{ fontSize: 'var(--fs-sm)', margin: 'var(--s-1) 0 0' }}
          >
            {t('collectedHint')}
          </p>
        )}
      </div>

      <button
        type="button"
        className="btn-primary"
        style={{ width: '100%', marginTop: 'var(--s-4)' }}
        disabled={busy}
        data-loading={busy ? 'true' : undefined}
        onClick={() => void submit()}
      >
        {t('confirmDelivery')}
      </button>
    </main>
  );
}
